// src/views/auth/signIn/index.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { login } from "../../../services/auth";
import { useAuth } from "../../../contexts/AuthContext";

const heroTitleLines = [
  "로그인하여",
  "예상공사비를 예측하세요😃",
];
const heroSubtitle = "공사 개요를 입력하면 AI가 예상 공사비를 산출해드립니다.";

export default function SignIn() {
  const textColor = useColorModeValue("#0f172a", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.300");
  const highlightColor = useColorModeValue("#2563eb", "#8b99ff");
  const heroFontSize = useBreakpointValue({ base: "28px", md: "32px", lg: "36px" });

  const [show, setShow] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [rememberEmail, setRememberEmail] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [heroLoaded, setHeroLoaded] = React.useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();

  React.useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }
  }, []);

  React.useEffect(() => {
    if (rememberEmail && email.trim()) {
      localStorage.setItem("rememberedEmail", email.trim());
    } else if (!rememberEmail) {
      localStorage.removeItem("rememberedEmail");
    }
  }, [rememberEmail, email]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !pw) {
      toast({ status: "warning", title: "이메일과 비밀번호를 모두 입력하세요." });
      return;
    }

    setLoading(true);
    try {
      const { token, user } = await login(email.trim(), pw);
      setToken(token);
      setUser(user || {});
      navigate("/admin", { replace: true });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        "이메일 또는 비밀번호를 확인하세요.";

      toast({
        status: "error",
        title: "로그인 실패",
        description: String(message),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bgGradient="linear(to-b, #eef2ff, #fefefe)" px={{ base: 4, md: 6 }}>
      <Flex minH="100vh" align="center" justify="center" py={{ base: 6, md: 10 }}>
        <Box w="full" maxW="1024px">
          <Box
            bg="white"
            borderRadius={{ base: "24px", md: "32px" }}
            borderWidth="1px"
            borderColor="rgba(15, 23, 42, 0.08)"
            boxShadow="0 40px 90px rgba(15, 23, 42, 0.18)"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", lg: "row" }}
              gap={{ base: 0, lg: 1 }}
              align="stretch"
            >
              <Box
                flex={{ base: "none", lg: 1 }}
                display="flex"
                p={{ base: 8, lg: 10 }}
                bg="white"
                borderRight={{ base: "none", lg: "1px solid rgba(15, 23, 42, 0.08)" }}
              >
                <Stack spacing={6} flex="1">
                  <Box>
                    <Text
                      fontSize={heroFontSize}
                      fontWeight="700"
                      lineHeight="1.2"
                      color={textColor}
                    >
                      {heroTitleLines.map((line) => (
                        <Text as="span" key={line} display="block">
                          {line}
                        </Text>
                      ))}
                    </Text>
                    <Text mt={2} color={textColorSecondary} fontSize={{ base: "13px", md: "13px" }}>
                      {heroSubtitle}
                    </Text>
                  </Box>

                  <Box
                    borderRadius="28px"
                    overflow="hidden"
                    bg="white"
                    boxShadow="0 25px 50px rgba(37, 99, 235, 0.25)"
                  >
                    <AspectRatio ratio={4 / 3}>
                      <Image
                        src="/starter.gif"
                        alt="프로젝트 스타터"
                        objectFit="cover"
                        opacity={heroLoaded ? 1 : 0}
                        transition="opacity 0.7s ease"
                        onLoad={() => setHeroLoaded(true)}
                        draggable={false}
                      />
                    </AspectRatio>
                  </Box>

                  {/* <Text color="gray.500" fontSize="13px">
                    팀과 함께하는 모든 프로젝트를 한눈에 확인하고 관리할 수 있습니다.
                  </Text> */}
                </Stack>
              </Box>

              <Box
                flex={{ base: "none", lg: 1 }}
                p={{ base: 8, md: 10 }}
                bg="white"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Stack spacing={5}>
                  {/* <Text fontSize="28px" fontWeight="700" color={textColor}>
                    로그인
                  </Text>
                  <Text fontSize="sm" color={textColorSecondary}>
                    가입된 이메일로 로그인하고 서비스를 시작하세요.
                  </Text> */}

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
                          아이디
                        </FormLabel>
                        <Input
                          type="email"
                          placeholder="이메일을 입력하세요."
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          h="52px"
                          fontSize="md"
                          px="16px"
                          borderRadius="16px"
                          bg="gray.50"
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
                          비밀번호
                        </FormLabel>
                        <InputGroup size="md">
                          <Input
                            type={show ? "text" : "password"}
                            placeholder="비밀번호를 입력하세요."
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            h="52px"
                            fontSize="md"
                            pr="48px"
                            borderRadius="16px"
                            bg="gray.50"
                          />
                          <InputRightElement>
                            <Icon
                              as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                              w={5}
                              h={5}
                              color="gray.500"
                              cursor="pointer"
                              onClick={() => setShow((prev) => !prev)}
                            />
                          </InputRightElement>
                        </InputGroup>
                      </FormControl>

                      <Flex align="center" pt={1}>
                        <Checkbox
                          isChecked={rememberEmail}
                          onChange={(e) => setRememberEmail(e.target.checked)}
                          colorScheme="blue"
                          size="md"
                        >
                          <Text fontSize="sm" color={textColorSecondary} fontWeight="500">
                            이메일 기억하기
                          </Text>
                        </Checkbox>
                      </Flex>

                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize="xs"
                        color={highlightColor}
                        pt={1}
                      >
                        <NavLink to="/auth/forgot-password">
                          <Text fontWeight="600" _hover={{ textDecor: "underline" }}>
                            비밀번호 재설정
                          </Text>
                        </NavLink>
                        <NavLink to="/auth/sign-up">
                          <Text fontWeight="600" _hover={{ textDecor: "underline" }}>
                            회원가입
                          </Text>
                        </NavLink>
                      </Flex>

                      <Button
                        type="submit"
                        h="52px"
                        fontSize="sm"
                        fontWeight="600"
                        isLoading={loading}
                        bgGradient="linear(to-r, #4f46e5, #1d4ed8)"
                        color="white"
                        borderRadius="16px"
                        _hover={{ bgGradient: "linear(to-r, #4338ca, #1d40af)" }}
                        boxShadow="0 20px 30px rgba(37, 99, 235, 0.25)"
                      >
                        로그인
                      </Button>
                    </Stack>
                  </form>
                </Stack>
              </Box>
            </Flex>
          </Box>

          <Text mt={6} fontSize="12px" color="gray.400" textAlign="center">
            © XI S&D, All Rights Reserved. Designed & Developed by DX Team.
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}